import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { ThemeProvider } from "@/theme/ThemeContext";
import { ContentProvider, useContent } from "@/context/ContentContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Resources from "@/pages/Resources";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Faq from "@/pages/Faq";
import Book from "@/pages/Book";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import NotFound from "@/pages/NotFound";
import AdminApp from "@/admin/AdminApp";

const SEO_DEFAULTS = {
  "/": ["Evol & Troppus — Parent Coaching & Family Support", "Evidence-informed parent coaching, home routine coaching, school collaboration support, and practical tools that help caregivers navigate everyday challenges with confidence."],
  "/about": ["About — Evol & Troppus", "Evol & Troppus empowers caregivers through education, practical strategies, and collaboration — delivered by a certified Behavior Technician (BT/RBT)."],
  "/services": ["Services — Evol & Troppus", "Parent coaching, home routine coaching, school collaboration support, virtual family coaching, and a free resource library for caregivers."],
  "/resources": ["Free Resources — Evol & Troppus", "Downloadable guides, visual supports, templates, and trackers that make evidence-informed strategies easier to use at home."],
  "/blog": ["Blog — Evol & Troppus", "Practical, evidence-informed writing on behavior, parenting, emotional regulation, and family routines."],
  "/faq": ["FAQ — Evol & Troppus", "Answers about our coaching services, approach, qualifications, and booking."],
  "/book": ["Book a Consultation — Evol & Troppus", "Schedule a free discovery call or a coaching consultation with Evol & Troppus."],
  "/contact": ["Contact — Evol & Troppus", "Questions about parent coaching or family support? Send us a message — a real person reads every one."],
  "/privacy": ["Privacy Policy — Evol & Troppus", "How Evol & Troppus collects, uses, and protects your information."],
};

function SEOHandler() {
  const { pathname } = useLocation();
  const { seo } = useContent();
  useEffect(() => {
    const key = SEO_DEFAULTS[pathname]
      ? pathname
      : pathname.startsWith("/services") ? "/services"
      : pathname.startsWith("/blog") ? "/blog"
      : "/";
    const [defTitle, defDesc] = SEO_DEFAULTS[key];
    const override = seo?.[key];
    document.title = override?.title || defTitle;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", override?.description || defDesc);
  }, [pathname, seo]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminApp />} />
      </Routes>
    );
  }
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <AnnouncementBar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/book" element={<Book />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <ThemeProvider>
      <ContentProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SEOHandler />
          <Shell />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </ContentProvider>
    </ThemeProvider>
  );
}

export default App;
